import { useState, useEffect, useCallback } from 'react';
import { apiGet } from '../utils/api';

/**
 * Hook pour récupérer et gérer le token Spotify depuis le backend
 * Le token est stocké côté serveur et récupéré via un endpoint sécurisé
 *
 * @returns {Object} État du token Spotify
 * @returns {string|null} spotifyToken - Le token d'accès Spotify
 * @returns {boolean} loading - Indique si le token est en cours de chargement
 * @returns {string|null} error - Message d'erreur si la récupération échoue
 * @returns {boolean} isExpired - Indique si le token est expiré
 * @returns {Object|null} userData - Données utilisateur Spotify
 * @returns {Function} refreshToken - Fonction pour rafraîchir le token
 */
export function useSpotifyToken() {
  const [spotifyToken, setSpotifyToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpired, setIsExpired] = useState(false);
  const [userData, setUserData] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);

  /**
   * Récupère le token Spotify depuis le backend
   */
  const fetchToken = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiGet('/spotify/token');

      if (response.success) {
        setSpotifyToken(response.accessToken);
        setExpiresAt(response.expiresAt);
        setUserData(response.userData);
        setIsExpired(false);
      } else {
        setSpotifyToken(null);
        setError(response.error || 'Erreur lors de la récupération du token');
        setIsExpired(response.expired || false);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération du token Spotify:', err);
      setSpotifyToken(null);
      setError(err.message || 'Erreur de connexion au serveur');

      // Vérifier si c'est une erreur d'expiration
      if (err.message?.includes('expiré') || err.message?.includes('expired')) {
        setIsExpired(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Rafraîchit le token Spotify
   */
  const refreshToken = useCallback(async () => {
    await fetchToken();
  }, [fetchToken]);

  // Récupérer le token au montage du composant
  useEffect(() => {
    fetchToken();
  }, [fetchToken]);

  // Vérifier périodiquement si le token est proche de l'expiration
  useEffect(() => {
    if (!expiresAt) return;

    const checkExpiration = () => {
      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;

      // Si le token expire dans moins de 5 minutes, le marquer comme expiré
      if (timeUntilExpiry < 5 * 60 * 1000) {
        setIsExpired(true);
        // Tenter de rafraîchir automatiquement
        fetchToken();
      }
    };

    // Vérifier toutes les minutes
    const interval = setInterval(checkExpiration, 60 * 1000);

    // Vérifier immédiatement
    checkExpiration();

    return () => clearInterval(interval);
  }, [expiresAt, fetchToken]);

  return {
    spotifyToken,
    loading,
    error,
    isExpired,
    userData,
    expiresAt,
    refreshToken,
  };
}

export default useSpotifyToken;
